/**
 * Persist the Patient Panel provider roster selection per signed-in oid.
 *
 * Stores composite `(provKeyId, ipa)` keys (and optional npi) so a multi-IPA
 * restore does not collapse two entitlements that share a ProvKeyId. Keys are
 * device-local only — same pattern as npiStorage / ipaStorage.
 */

const STORAGE_PREFIX = "member-360:worklist-providers:";

export interface StoredProviderPick {
  provKeyId: string;
  ipa: string;
  npi?: string;
}

function storageKey(oid: string): string {
  return `${STORAGE_PREFIX}${oid}`;
}

export function getStoredProviderPicks(oid: string | undefined): StoredProviderPick[] {
  if (!oid) return [];
  try {
    const raw = localStorage.getItem(storageKey(oid));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((entry): StoredProviderPick | null => {
        const e = entry as { provKeyId?: unknown; ipa?: unknown; npi?: unknown };
        const provKeyId = typeof e.provKeyId === "string" ? e.provKeyId.trim() : "";
        const ipa = typeof e.ipa === "string" ? e.ipa.trim() : "";
        if (!provKeyId || !ipa) return null;
        const npi = typeof e.npi === "string" ? e.npi.trim() : undefined;
        return npi ? { provKeyId, ipa, npi } : { provKeyId, ipa };
      })
      .filter((p): p is StoredProviderPick => p != null);
  } catch {
    return [];
  }
}

export function setStoredProviderPicks(
  oid: string | undefined,
  picks: StoredProviderPick[],
): void {
  if (!oid) return;
  try {
    if (picks.length === 0) {
      localStorage.removeItem(storageKey(oid));
      return;
    }
    localStorage.setItem(storageKey(oid), JSON.stringify(picks));
  } catch {
    // Quota / private mode — selection still works in-memory for the session.
  }
}

export function clearStoredProviderPicks(oid: string | undefined): void {
  if (!oid) return;
  try {
    localStorage.removeItem(storageKey(oid));
  } catch {
    // ignore
  }
}
